import { useEffect, useLayoutEffect, type RefObject } from "react";

/*
 * A floating layer — a Select's listbox, a Popover's menu — rendered into
 * <body> with position: fixed, so no `overflow: hidden` ancestor (a Sheet, a
 * rounded card, an accordion) can cut it off. It opens below its trigger, or
 * above when there is more room there, and closes rather than drift when the
 * page scrolls. A resize (a phone's keyboard opening) moves it instead.
 */

/** Marks a layer, so a layer opened from inside another doesn't close its parent. */
export const LAYER_ATTR = "data-kk-layer";

const EDGE = 8;
const GAP = 6;
const ROOM = 12;
const MIN_HEIGHT = 140;

type Place = {
  align?: "left" | "right";
  /** The layer is at least the trigger's width, and at least this. */
  minWidth?: number;
  /** Stretch to the trigger's width (a listbox), rather than the content's (a menu). */
  matchWidth?: boolean;
  /** Tallest the layer may be; it scrolls past that. */
  maxHeight?: number;
  /** Changes when the content changes size (a search narrowing a list): measure again. */
  measure?: unknown;
};

function place(anchor: HTMLElement, layer: HTMLElement, p: Place) {
  const r = anchor.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  layer.style.maxHeight = "";
  if (p.matchWidth) layer.style.width = `${Math.min(vw - EDGE * 2, Math.max(r.width, p.minWidth ?? 0))}px`;
  const w = layer.offsetWidth;
  const natural = layer.scrollHeight;

  const below = vh - r.bottom - ROOM;
  const above = r.top - ROOM;
  const cap = p.maxHeight ?? Infinity;
  const want = Math.min(natural, cap);
  const up = below < want && above > below;
  const height = Math.min(want, Math.max(MIN_HEIGHT, Math.min(cap, up ? above : below)));

  const left = p.align === "right" ? r.right - w : r.left;
  layer.style.left = `${Math.max(EDGE, Math.min(left, vw - w - EDGE))}px`;
  layer.style.top = `${up ? r.top - GAP - height : r.bottom + GAP}px`;
  layer.style.maxHeight = `${height}px`;
  layer.style.visibility = "visible";
}

/**
 * Positions `layer` against `anchor` while open, and calls `onClose` on a
 * pointer down outside both or a scroll anywhere but inside the layer.
 */
export function useLayer(open: boolean, anchor: RefObject<HTMLElement | null>, layer: RefObject<HTMLElement | null>, onClose: () => void, p: Place = {}) {
  const { align, minWidth, matchWidth, maxHeight, measure } = p;

  // Only on open and when the content's size changes: measuring resets the
  // height, which would throw away a list's scroll position on every hover.
  useLayoutEffect(() => {
    if (open && anchor.current && layer.current) place(anchor.current, layer.current, { align, minWidth, matchWidth, maxHeight });
  }, [open, measure, anchor, layer, align, minWidth, matchWidth, maxHeight]);

  useEffect(() => {
    if (!open) return;
    const inside = (t: EventTarget | null) => {
      const n = t as Node | null;
      if (!n) return false;
      if (anchor.current?.contains(n) || layer.current?.contains(n)) return true;
      // A layer opened from inside this one (a Select in a menu) belongs to it.
      const other = n instanceof Element ? n.closest(`[${LAYER_ATTR}]`) : null;
      return !!other && other !== layer.current;
    };
    const onDown = (e: PointerEvent) => !inside(e.target) && onClose();
    const onScroll = (e: Event) => !layer.current?.contains(e.target as Node) && onClose();
    const onResize = () => anchor.current && layer.current && place(anchor.current, layer.current, { align, minWidth, matchWidth, maxHeight });
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, anchor, layer, onClose, align, minWidth, matchWidth, maxHeight]);
}

/** A mouse or trackpad: focusing a field won't pull up an on-screen keyboard. */
export const finePointer = () => typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;
